/** @format */

import {Trans, useTranslation} from 'react-i18next';

import {OauthProvider} from 'graphql/generated/types';

import ProviderTabs from '../ProviderTabs';
import YandexAccountsIllustration from './YandexAccountsIllustration';
import {
  Container,
  ContentRow,
  InstructionBlock,
  InstructionText,
  EmergencyRow,
  EmergencyIcon,
  SafetyBlock,
  SafetyTextColumn,
  SafetyTitle,
  SafetyText,
  SafetyIconWrapper,
  ConnectButton,
  ButtonIcon,
  YandexHintBlock,
  YandexHintText,
} from './styles';

const PROVIDER_WINDOW_NAME: Partial<Record<OauthProvider, string>> = {
  YANDEX: 'Яндекса',
  VKONTAKTE: 'VK',
  VKONTAKTE_ADS: 'VK',
  GOOGLE: 'Google',
};

interface Props {
  provider: OauthProvider;
  onConnect: (provider: OauthProvider) => void;
  activeProvider?: OauthProvider | null;
  onProviderClick?: (provider: OauthProvider | null) => void;
}

const AdSystemConnectBlock = ({provider, onConnect, activeProvider, onProviderClick}: Props) => {
  const {t} = useTranslation();

  const hasTabs = activeProvider !== undefined && onProviderClick != null;
  const showContent = !hasTabs || activeProvider != null;

  const providerWindowName = PROVIDER_WINDOW_NAME[provider] || provider;
  const buttonText = t('Выдать доступ в окне {{provider}}', {provider: providerWindowName});

  const isYandex = provider === 'YANDEX';

  const handleConnect = () => {
    onConnect(provider);
  };

  return (
    <Container data-testid='ad-system-connect-block'>
      {hasTabs && (
        <ProviderTabs activeProvider={activeProvider!} onProviderClick={onProviderClick} />
      )}
      {showContent && (
        <>
          <ContentRow>
            <InstructionBlock>
              <InstructionText>
                <Trans>Разрешите доступ к кабинету, чтобы наш движок смог начать работать.</Trans>
              </InstructionText>

              <EmergencyRow>
                <EmergencyIcon component='span' className='material-symbols-outlined'>
                  emergency
                </EmergencyIcon>
                <InstructionText>
                  <Trans>
                    Процесс выдачи доступа произойдет в отдельном окне, после которого мы вернемся к
                    настройке.
                  </Trans>
                </InstructionText>
              </EmergencyRow>

              {isYandex && (
                <YandexHintBlock>
                  <YandexHintText>
                    <Trans>
                      Если у вас несколько аккаунтов Яндекса, выберите тот, где подключён Директ
                    </Trans>
                  </YandexHintText>
                  <YandexAccountsIllustration />
                </YandexHintBlock>
              )}
            </InstructionBlock>

            <SafetyBlock>
              <SafetyTextColumn>
                <SafetyTitle>
                  <Trans>Это безопасно</Trans>
                </SafetyTitle>
                <SafetyText>
                  <Trans>
                    Без вашего разрешения мы не внесем никаких изменений в ваши рекламные кампании,
                    лишь оценим содержимое и покажем отчет о состоянии
                  </Trans>
                </SafetyText>
              </SafetyTextColumn>
              <SafetyIconWrapper component='span' className='material-symbols-outlined'>
                encrypted
              </SafetyIconWrapper>
            </SafetyBlock>
          </ContentRow>

          <ConnectButton onClick={handleConnect}>
            <ButtonIcon component='span' className='material-symbols-outlined'>
              arrow_outward
            </ButtonIcon>
            {buttonText}
          </ConnectButton>
        </>
      )}
    </Container>
  );
};

export default AdSystemConnectBlock;
